import { AdminLayout } from "@/pages/AdminPages";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const SAMPLE_BIDDING = `{
  "external_id": "GZB-2026-0001",
  "title": "国网江苏省电力有限公司2026年第一次配网设备公开招标",
  "summary": "本次招标范围包括 10kV 配电变压器 200 台...",
  "content_html": "<p>详见招标文件...</p>",
  "source_url": "https://ecp.sgcc.com.cn/...",
  "published_at": "2026-01-15T08:00:00+08:00",
  "category_custom_id": "GUOWANG_BIDDING",
  "dynamic_fields": {
    "type": "招标公告",
    "province": "江苏",
    "year": 2026,
    "bidder": "国网江苏",
    "bid_no": "GZB-2026-0001"
  },
  "attachments": [
    { "url": "https://example.com/file.pdf", "filename": "招标文件.pdf", "mime": "application/pdf" }
  ]
}`;

const SAMPLE_WINNER = `{
  "external_id": "GZB-2026-0001-RES",
  "title": "国网江苏 2026 年第一次配网设备中标公告",
  "category_custom_id": "WINNER_BID",
  "published_at": "2026-02-20T15:00:00+08:00",
  "dynamic_fields": {
    "type": "中标公告",
    "province": "江苏",
    "year": 2026,
    "bidder": "国网江苏",
    "winner": "金风科技股份有限公司",
    "amount": "2.35 亿"
  }
}`;

const SAMPLE_CANDIDATE = `{
  "external_id": "GZB-2026-0001-CAND",
  "title": "国网江苏 2026 年第一次配网设备中标候选人公示",
  "category_custom_id": "WINNER_CANDIDATE",
  "dynamic_fields": {
    "type": "中标候选人",
    "province": "江苏",
    "year": 2026,
    "bidder": "国网江苏",
    "winner": "金风科技股份有限公司",
    "ranking": "第一名"
  }
}`;

export function AdminCrawlerDocsPage() {
  return (
    <AdminLayout>
      <div className="space-y-4 max-w-4xl">
        <div>
          <h1 className="text-2xl font-bold">爬虫开发对接文档</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            外部爬虫通过 HTTP API 把抓取结果上报本平台。所有请求需在 Header 携带 <code className="px-1 bg-muted rounded">X-API-Key</code>。
          </p>
        </div>

        <Card>
          <CardHeader><CardTitle>1. 鉴权 · API Key</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>在「<a href="#/admin/api-keys" className="text-primary hover:underline">API 密钥</a>」页签发密钥时勾选允许的栏目（含虚拟栏目 <code>WINNER_BID</code> / <code>WINNER_CANDIDATE</code>）。密钥仅在创建时显示一次，请立即保存。</p>
            <pre className="bg-muted rounded p-3 text-xs overflow-x-auto"><code>{`Authorization: Bearer <可选 user token，仅管理操作需要>
X-API-Key: ztb_live_xxxxxxxxxxxxxxxx
Content-Type: application/json`}</code></pre>
            <ul className="list-disc pl-5 space-y-1">
              <li>默认限速 60 / 分钟（管理员可改）</li>
              <li>密钥泄漏后立即在后台「吊销」，已签发请求会被拒绝</li>
              <li>建议为每个数据源单独签发密钥，便于审计</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>2. 单条上报 · POST /api/ingest/projects</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p>用于增量上报。<code>external_id</code> 为外部唯一编号，用于幂等去重。</p>
            <Field label="请求体（招标公告）">
              <pre className="bg-muted rounded p-3 text-xs overflow-x-auto"><code>{SAMPLE_BIDDING}</code></pre>
            </Field>
            <Field label="请求体（中标公告 → 走虚拟栏目）">
              <pre className="bg-muted rounded p-3 text-xs overflow-x-auto"><code>{SAMPLE_WINNER}</code></pre>
            </Field>
            <Field label="请求体（中标候选人公示）">
              <pre className="bg-muted rounded p-3 text-xs overflow-x-auto"><code>{SAMPLE_CANDIDATE}</code></pre>
            </Field>
            <p className="text-muted-foreground">
              响应示例：<code>{"{ \"id\": \"<uuid>\", \"created\": true }"}</code>。同一 <code>external_id</code> 重复上报视为更新。
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>3. 批量上报 · POST /api/ingest/projects/bulk</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>请求体为数组，<strong>建议每批 ≤ 50 条</strong>。失败条目会逐条返回原因。</p>
            <pre className="bg-muted rounded p-3 text-xs overflow-x-auto"><code>{`POST /api/ingest/projects/bulk
[
  { "external_id": "...", "title": "...", "category_custom_id": "...", ... },
  { ... }
]

Response:
{
  "ok": 47,
  "failed": [
    { "index": 12, "external_id": "X-9", "error": "category not found" }
  ]
}`}</code></pre>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>4. 字段规范</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="border-b text-left">
                <th className="py-1.5 pr-3">字段</th><th className="pr-3">类型</th><th className="pr-3">必填</th><th>说明</th>
              </tr></thead>
              <tbody>
                {[
                  ["external_id","string","是","外部唯一 ID（用于幂等）"],
                  ["title","string","是","原始标题，去除多余空白"],
                  ["category_custom_id","string","是","栏目编号，含虚拟 WINNER_*"],
                  ["summary","string","否","摘要 / 节选"],
                  ["content_html","string","否","正文 HTML（仅保留安全标签）"],
                  ["source_url","string","建议","原始链接（VIP 锁定时不向访客暴露）"],
                  ["published_at","ISO-8601","建议","发布时间（带时区）"],
                  ["dynamic_fields.type","string","建议","招标公告 / 中标公告 / 中标候选人 / 答疑变更 等"],
                  ["dynamic_fields.province","string","建议","用于「省份分布」与筛选"],
                  ["dynamic_fields.year","int","建议","用于年度归档"],
                  ["dynamic_fields.bidder","string","建议","招标方简称"],
                  ["dynamic_fields.winner","string","中标类","中标人名称"],
                  ["dynamic_fields.amount","string","建议","金额（保留单位，如 \"2.35 亿\"）"],
                  ["attachments[]","array","否","附件，存入 S3 / 对象存储后再下发"],
                ].map(([f, t, req, desc]) => (
                  <tr key={f} className="border-b border-muted">
                    <td className="py-1.5 pr-3 font-mono text-[11px]">{f}</td>
                    <td className="pr-3 text-muted-foreground">{t}</td>
                    <td className="pr-3">{req === "是" ? <Badge className="text-[10px]">必填</Badge> : <span className="text-muted-foreground">{req}</span>}</td>
                    <td className="text-muted-foreground">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>5. curl 示例</CardTitle></CardHeader>
          <CardContent className="text-sm">
            <pre className="bg-muted rounded p-3 text-xs overflow-x-auto"><code>{`curl -X POST https://example.com/api/ingest/projects \\
  -H "X-API-Key: ztb_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -d @project.json`}</code></pre>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>6. 最佳实践</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1.5">
            <ul className="list-disc pl-5 space-y-1">
              <li>每个数据源单独密钥；指数退避重试 5xx；4xx 立即定位字段问题</li>
              <li>HTML 入库前清洗 <code>&lt;script&gt;</code> / 内联事件 / 远程图床；建议把图片镜像到本站对象存储</li>
              <li><code>published_at</code> 缺失时优先解析「发布时间」/「公告时间」字段，最后才用抓取时间</li>
              <li>同一项目从「招标 → 候选人 → 中标公告」是 3 个独立条目，<code>external_id</code> 互不复用</li>
              <li>金额规范化：保留原始字符串 + 同时输出 <code>amount_cents</code>（int，可选）便于后续聚合</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>7. S3 / 对象存储附件流程</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-3">
            <p className="text-muted-foreground">
              附件需先上传到对象存储（S3 / OSS / R2 等）再随项目一起提交。系统使用「后台 → S3 配置」中的桶 / Endpoint
              生成预签名 URL，爬虫端按下面三步完成：
            </p>
            <div>
              <div className="font-medium mb-1">① 申请预签名 PUT URL</div>
              <pre className="bg-muted rounded p-3 text-xs overflow-x-auto"><code>{`curl -X POST https://example.com/api/ingest/attachments/presign \\
  -H "X-API-Key: ztb_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "filename": "tender.pdf",
    "mime": "application/pdf",
    "size_bytes": 524288
  }'

# 响应:
# {
#   "s3_key":     "ingest/2026/01/abcd1234-tender.pdf",
#   "upload_url": "https://bucket.s3.amazonaws.com/...&X-Amz-Signature=...",
#   "expires_in": 600
# }`}</code></pre>
            </div>
            <div>
              <div className="font-medium mb-1">② 直接 PUT 到对象存储</div>
              <pre className="bg-muted rounded p-3 text-xs overflow-x-auto"><code>{`curl -X PUT "$UPLOAD_URL" \\
  -H "Content-Type: application/pdf" \\
  --data-binary @tender.pdf`}</code></pre>
              <p className="text-xs text-muted-foreground mt-1">注意 <code>Content-Type</code> 必须与申请时一致，否则签名失败。</p>
            </div>
            <div>
              <div className="font-medium mb-1">③ 提交项目时引用 s3_key</div>
              <pre className="bg-muted rounded p-3 text-xs overflow-x-auto"><code>{`curl -X POST https://example.com/api/ingest/projects \\
  -H "X-API-Key: ztb_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_id": "ZB-2026-001",
    "title":       "某市政道路工程招标公告",
    "category_custom_id": "tender",
    "published_at": "2026-01-15T08:00:00+08:00",
    "attachments": [
      {
        "filename":   "tender.pdf",
        "mime":       "application/pdf",
        "size_bytes": 524288,
        "s3_key":     "ingest/2026/01/abcd1234-tender.pdf"
      }
    ]
  }'`}</code></pre>
            </div>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>已经有公网 URL 的附件可直接把 <code>s3_key</code> 写成 <code>http(s)://...</code>，下载时会 302 跳转</li>
              <li>预签名链接默认 10 分钟有效；大文件（&gt;100MB）建议改用分片上传或 server-side fetch</li>
              <li>下载时由 <code>/api/attachments/&#123;id&#125;/download</code> 重新签发 GET 链接，不向访客暴露原始桶地址</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  );
}
